import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { all_routes } from "../../Router/all_routes";
import { ArrowLeft, ChevronDown, Info, Filter } from "feather-icons-react/build/IconComponents";
import useProducts from "../../hooks/useProducts";
import Table from "../../core/pagination/datatable";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { createStockMove } from "../../api/inventoryApi";
import Swal from "sweetalert2";

const StockMove = () => {
  // On récupère les données via le hook en fournissant des valeurs par défaut
  const { 
    products = [], 
    categoryData = [], 
    stockMoves = [] 
  } = useProducts();
  
  const route = all_routes;

  /** États pour les filtres de la liste **/
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedOperationFilter, setSelectedOperationFilter] = useState(null);

  /** États pour le formulaire de création **/
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [movementQuantity, setMovementQuantity] = useState("");

  /** États pour la gestion du chargement et des erreurs **/
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  /** Options statiques pour les stocks **/
  const storeOptions = [
    { value: "Bar", label: "Bar" },
    { value: "Exposition", label: "Exposition" },
    { value: "Cuisine", label: "Cuisine" },
    { value: "Stock général", label: "Stock général" },
  ];

  /** Préparation des options pour les catégories **/
  const categoryOptions = categoryData.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  /** Filtrage des produits en fonction du stock sélectionné **/
  const filteredProducts = selectedStore
    ? products.filter((product) => product.stock === selectedStore.value)
    : products;

  /** Options pour le sélecteur de produits **/
  const productOptions = filteredProducts.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  /** Options pour l’opération (pour le formulaire et le filtre) **/
  const moveOptions = [
    { value: "vente", label: "Vente" },
    { value: "entrée", label: "Entrée" },
    { value: "sortie", label: "Sortie" },
  ];

  /** Lorsqu'on change de stock, on vérifie que le produit sélectionné appartient bien à ce stock **/
  useEffect(() => {
    if (selectedStore && selectedProduct) {
      const stillValid = filteredProducts.some(
        (prod) => prod.id === selectedProduct.value
      );
      if (!stillValid) {
        setSelectedProduct(null);
      }
    }
  }, [selectedStore, selectedProduct, filteredProducts]);

  /** Gestion du submit du formulaire de création d'un mouvement **/
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    // Vérification que tous les champs obligatoires sont renseignés
    if (!selectedStore || !selectedProduct || !selectedOperation || !movementQuantity) {
      setError("Veuillez remplir tous les champs.");
      setLoading(false);
      return;
    }
    // Constitution du payload (adapter selon les besoins de votre API)
    const moveData = {
      product_id: selectedProduct.value,
      move_operation: selectedOperation.value,
      quantity_move: Number(movementQuantity),
    };

    try {
      // Simulation d'un appel API (remplacer par fetch/axios)
      const response = await createStockMove(moveData);
      // Réinitialisation du formulaire en cas de succès
      console.log(response);
      setSelectedStore(null);
      setSelectedProduct(null);
      setSelectedOperation(null);
      setMovementQuantity("");

      Swal.fire("Création !", "Mouvement effectué avec succès !", "success");
      
    } catch (err) {
      setError("Erreur lors de l'enregistrement." );
    }
    setLoading(false);
  };

  /** Filtrage combiné pour la liste des mouvements **/
  const filteroperations = useMemo(() => {
    return (stockMoves || []).filter((stockMove) => {
      const productName = stockMove.product?.name || "";
      const matchesName = searchTerm
        ? productName.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchesOperation = selectedOperationFilter
        ? stockMove.move_operation === selectedOperationFilter.value
        : true;
      const matchesStock = selectedStock
        ? stockMove.product?.stock === selectedStock.value
        : true;
      const matchesCategory = selectedCategory
        ? stockMove.product?.category_id &&
          stockMove.product.category_id === selectedCategory.value
        : true;
      return matchesName && matchesOperation && matchesStock && matchesCategory;
    });
  }, [stockMoves, searchTerm, selectedOperationFilter, selectedStock, selectedCategory]);

  /** Toggle de la visibilité des filtres **/
  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

 /** Définition des colonnes du tableau **/
  const columns = [
    {
      title: "Effectué par",
      dataIndex: "user",
      render: (user) => user?.name || "Non défini",
    },
    {
      title: "Produit",
      dataIndex: "product",
      render: (product) => product?.name || "Non défini",
    },
    {
      title: "Stock",
      dataIndex: "product",
      render: (product) => product?.stock || "Non défini",
    },
    {
      title: "Prix vente",
      dataIndex: "product",
      render: (product) => product?.variations?.[0]?.prix_vente || "Non défini",
    },
    {
      title: "Prix achat",
      dataIndex: "product",
      render: (product) => product?.variations?.[0]?.prix_achat || "Non défini",
    },
    {
      title: "Quantité",
      dataIndex: "quantity_move",
      key: "quantity_move",
    },
    {
      title: "Mouvement",
      dataIndex: "move_operation",
      key: "move_operation",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      render: (text,record) => {
        const date = new Date(record.created_at);
        return date.toLocaleDateString("fr-FR", {
          minute: "numeric",
          hour: "numeric",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      },
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        {/* SECTION: Création d'un mouvement */}
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Enregistrer un Mouvement</h4>
                <h6>Gérer le mouvement des produits</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <div className="page-btn">
                  <Link to={route.productlist} className="btn btn-secondary">
                    <ArrowLeft className="me-2" />
                    Retour aux produits
                  </Link>
                </div>
              </li>
            </ul>
          </div>
          {/* Fin de l'en-tête */}

          <form onSubmit={handleSubmit}>
            <div className="card">
              <div className="card-body add-product pb-0">
                {/* Accordéon: Informations sur le produit */}
                <div className="accordion-card-one accordion" id="accordionExample">
                  <div className="accordion-item">
                    <div className="accordion-header" id="headingOne">
                      <div
                        className="accordion-button"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseOne"
                        aria-controls="collapseOne"
                      >
                        <div className="addproduct-icon">
                          <h5>
                            <Info className="add-info" />
                            <span>Informations sur le produit</span>
                          </h5>
                          <Link to="#">
                            <ChevronDown className="chevron-down-add" />
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div
                      id="collapseOne"
                      className="accordion-collapse collapse show"
                      aria-labelledby="headingOne"
                      data-bs-parent="#accordionExample"
                    >
                      <div className="accordion-body">
                        <div className="row">
                          {/* Sélection du Stock */}
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3 add-product">
                              <label className="form-label">Stock</label>
                              <Select
                                classNamePrefix="react-select"
                                options={storeOptions}
                                placeholder="Choisissez le stock"
                                value={selectedStore}
                                onChange={setSelectedStore}
                              />
                            </div>
                          </div>
                          {/* Sélection du Produit (filtré en fonction du stock) */}
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3 add-product">
                              <div className="add-newplus">
                                <label className="form-label">Produits</label>
                              </div>
                              <Select
                                classNamePrefix="react-select"
                                options={productOptions}
                                placeholder="Choisir"
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          {/* Sélection de l'opération */}
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="mb-3 add-product">
                              <div className="add-newplus">
                                <label className="form-label">Opération</label>
                              </div>
                              <Select
                                classNamePrefix="react-select"
                                options={moveOptions}
                                placeholder="Choisir"
                                value={selectedOperation}
                                onChange={setSelectedOperation}
                              />
                            </div>
                          </div>
                          {/* Saisie de la quantité */}
                          <div className="col-lg-4 col-sm-6 col-12">
                            <div className="input-blocks add-product">
                              <label>Quantité</label>
                              <input
                                type="number"
                                min={0}
                                className="form-control"
                                placeholder="Entrer la quantité"
                                value={movementQuantity}
                                onChange={(e) => setMovementQuantity(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Fin de l'accordéon Informations sur le produit */}
              </div>
            </div>

            <div className="col-lg-12">
              {error && <div className="alert alert-danger mt-3">{error}</div>}
              <div className="btn-addproduct mb-4">
                <button type="submit" className="btn btn-success mt-3" disabled={loading}>
                  {loading ? "Enregistrement..." : "Effectuer"}
                </button>
              </div>
            </div>
          </form>
          {/* Fin du formulaire */}
        </div>

        <br /><br />

        {/* SECTION: Liste des mouvements */}
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Liste des Mouvements</h4>
              </div>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Rechercher un produit..."
                      className="form-control form-control-sm formsearch"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="search-path">
                  <button className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}>
                    <Filter className="filter-icon" onClick={toggleFilterVisibility} />
                    <span onClick={toggleFilterVisibility}>
                      <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                    </span>
                  </button>
                </div>
              </div>

              {isFilterVisible && (
                <div className="card filter-card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-lg-4">
                        <label>Catégorie</label>
                        <Select
                          options={categoryOptions}
                          value={selectedCategory}
                          onChange={setSelectedCategory}
                          placeholder="Choisir une catégorie"
                        />
                      </div>
                      <div className="col-lg-4">
                        <label>Stock</label>
                        <Select
                          options={storeOptions}
                          value={selectedStock}
                          onChange={setSelectedStock}
                          placeholder="Choisir un stock"
                        />
                      </div>
                      <div className="col-lg-4">
                        <label>Opération</label>
                        <Select
                          options={moveOptions}
                          value={selectedOperationFilter}
                          onChange={setSelectedOperationFilter}
                          placeholder="Choisir une opération"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="table-responsive">
                <Table columns={columns} dataSource={filteroperations} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockMove;
